/**
 * "How did the call go?" — five one-tap buttons after a call about a new
 * request (New requests, migration 718). The server keeps the score and
 * answers with the new try line; this component never counts on its own.
 *
 * "Talked, not now" asks one more thing: when to call back (a week, two
 * weeks, a month). Everything else saves on the first tap.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CALL_BACK_CHOICES, CALL_BUTTONS, type CallOutcome, type TriesSummary } from "@/lib/new-requests";

export const REQUEST_LIST_KEYS = [["book-my-test", "open"], ["requests"]] as const;

export function CallOutcomeButtons({
    bookingId,
    onLogged,
    compact = false,
}: {
    bookingId: string;
    onLogged?: (outcome: CallOutcome, tries: TriesSummary | undefined) => void;
    compact?: boolean;
}) {
    const qc = useQueryClient();
    const [askWhen, setAskWhen] = useState(false);
    const log = useMutation({
        mutationFn: (v: { outcome: CallOutcome; callBackDays?: number }) =>
            api.post<{ ok: true; tries?: TriesSummary }>(`/api/requests/${bookingId}/call`, v),
        onSuccess: (res, v) => {
            setAskWhen(false);
            onLogged?.(v.outcome, res?.tries);
        },
        onError: () => toast.error("Couldn't save how the call went. Try again."),
        onSettled: () => {
            for (const key of REQUEST_LIST_KEYS) void qc.invalidateQueries({ queryKey: [...key] });
        },
    });

    return (
        <div className={`cob${compact ? " compact" : ""}`} data-testid="call-outcome-buttons">
            <div className="cob-row" role="group" aria-label="How did the call go?">
                <span className="cob-q">How did the call go?</span>
                {CALL_BUTTONS.map((b) => (
                    <button
                        key={b.outcome}
                        type="button"
                        className={`cob-b${b.outcome === "talked_booked" ? " ok" : ""}`}
                        disabled={log.isPending}
                        onClick={() => {
                            if (b.outcome === "talked_not_now") setAskWhen(true);
                            else log.mutate({ outcome: b.outcome });
                        }}
                    >
                        {b.label}
                    </button>
                ))}
            </div>
            {askWhen && (
                <div className="cob-when" role="group" aria-label="When should we call back?">
                    <b>When should we call back?</b>
                    {CALL_BACK_CHOICES.map((c) => (
                        <button
                            key={c.days}
                            type="button"
                            className="cob-b"
                            disabled={log.isPending}
                            onClick={() => log.mutate({ outcome: "talked_not_now", callBackDays: c.days })}
                        >
                            {c.label}
                        </button>
                    ))}
                    <button type="button" className="cob-b quiet" onClick={() => setAskWhen(false)}>
                        Never mind
                    </button>
                </div>
            )}
        </div>
    );
}

/** Light, fixed colors like the rest of the toolbar flyout; also used on the New requests page. */
export const callOutcomeCss = `
  .cob{display:grid;gap:6px}
  .cob-row,.cob-when{display:flex;flex-wrap:wrap;gap:6px;align-items:center}
  .cob-q{font-size:12px;font-weight:700;color:#5d6b7e;margin-right:2px}
  .cob-when b{font-size:12.5px;color:#14213a;margin-right:4px}
  .cob-b{border:1.5px solid #D8CEBD;background:#fff;color:#14213a;border-radius:8px;padding:6px 10px;font-size:12.5px;font-weight:600;cursor:pointer}
  .cob-b:hover{border-color:#004AAD}
  .cob-b.ok{border-color:#2f7d4f;color:#2f7d4f;font-weight:700}
  .cob-b.quiet{border-color:transparent;color:#5d6b7e}
  .cob-b[disabled]{opacity:.6;cursor:wait}
  .cob-b:focus-visible{outline:3px solid #8fb4ee;outline-offset:2px}
  .cob.compact .cob-q{flex-basis:100%}
`;
